import { Dispatch, FC, useEffect, useState } from "react";
import {
  Button, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Stack
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import DoneIcon from "@mui/icons-material/Done";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { AxiosInstance } from "axios";
import { AlertType } from "../../../types/AlertType";
import { DataFacture } from "./TableauDeBordFacturation";

interface DialogDateValidationFactureProps {
  open: boolean;
  http: AxiosInstance;
  /**
   * Lignes à valider : une seule via le crayon du tableau, plusieurs via le
   * bouton « Valider la facturation » (validation en masse des lignes cochées).
   */
  factures: DataFacture[];
  onClose: () => void;
  /** Appelé après un enregistrement réussi : recharge la liste */
  onSuccess: () => void;
  setOpenSnack: Dispatch<React.SetStateAction<boolean>>;
  setAlerteMessage: Dispatch<React.SetStateAction<string>>;
  setAlertType: Dispatch<React.SetStateAction<AlertType>>;
}

/**
 * Pop-up d'ajout / modification de la date de validation d'une ou plusieurs
 * factures. Reprend le pop-up « Facture envoyée au client » : la date est
 * préremplie avec la date déjà affichée sur la ligne (crayon d'une ligne
 * unique), ou à défaut avec aujourd'hui.
 *
 * Côté back, l'enregistrement passe les saisies manager de la période au
 * statut « validée » et historise la date saisie, exactement comme le bouton
 * « Valider » du cumul des articles (écran Saisie agent).
 */
const DialogDateValidationFacture: FC<DialogDateValidationFactureProps> = ({
  open,
  http,
  factures,
  onClose,
  onSuccess,
  setOpenSnack,
  setAlerteMessage,
  setAlertType,
}) => {
  const [dateValidation, setDateValidation] = useState<Dayjs | null>(dayjs());
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const nbFactures: number = factures.length;

  // Date déjà saisie : seulement pour une ligne unique (crayon). En masse, on
  // part toujours de la date du jour.
  const dateExistante: string | null =
    nbFactures === 1 ? factures[0]?.date_validation ?? null : null;

  useEffect(() => {
    // Modification => on repart de la date existante, ajout => date du jour
    if (open) {
      setDateValidation(
        dateExistante ? dayjs(String(dateExistante).split(" ")[0]) : dayjs()
      );
    }
  }, [open, dateExistante]);

  const dateValide: boolean = Boolean(
    dateValidation && dateValidation.isValid()
  );

  const handleValider = () => {
    if (!dateValide || nbFactures === 0) {
      return;
    }

    setIsSaving(true);

    const data = {
      dateValidation: dateValidation?.format("YYYY-MM-DD"),
      // Le back regroupe par période x piv_oper : la devise n'intervient pas.
      factures: factures.map((facture) => ({
        piv_oper_socifact_codes_id: facture?.piv_oper_socifact_codes_id,
        mois: facture?.periode_mois,
        annee: facture?.periode_annee,
      })),
    };

    http
      .post("facturation/dashboard/date-validation", data)
      .then((res) => {
        const { validate, message } = res?.data ?? {};

        setAlerteMessage(message ?? "");
        setAlertType(validate ? "success" : "warning");
        setOpenSnack(true);

        if (validate) {
          onSuccess();
        }

        onClose();
      })
      .catch((error) => {
        console.log(error);
        setAlerteMessage("Désolé! Une erreur est survenue !");
        setAlertType("error");
        setOpenSnack(true);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Date de validation</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {nbFactures > 1
            ? `Saisissez la date de validation des ${nbFactures} factures sélectionnées.`
            : dateExistante
              ? "Modifiez la date de validation de la facture sélectionnée."
              : "Saisissez la date de validation de la facture sélectionnée."}
        </DialogContentText>
        <Stack sx={{ mt: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date de validation"
              format="DD/MM/YYYY"
              value={dateValidation}
              onChange={(nouvelleDate) => setDateValidation(nouvelleDate)}
              slotProps={{
                textField: {
                  size: "small",
                  color: "warning",
                  fullWidth: true,
                  error: !dateValide,
                  helperText: !dateValide ? "Date invalide" : "",
                },
              }}
            />
          </LocalizationProvider>
        </Stack>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          onClick={() => handleValider()}
          endIcon={<DoneIcon />}
          loading={isSaving}
          loadingPosition="end"
          disabled={!dateValide || nbFactures === 0}
          sx={{ textTransform: "none" }}
          variant="contained"
          color="warning"
        >
          Valider
        </LoadingButton>
        <Button
          onClick={() => onClose()}
          variant="contained"
          color="inherit"
          sx={{ textTransform: "none" }}
          startIcon={<ExitToAppIcon />}
        >
          Annuler
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogDateValidationFacture;
